import React, { Component } from "react";
import { Loader } from "react-loaders";
import _, { isNil } from "lodash";
import NumberFormat from "react-number-format";

import UiTitle from "../components/finance/decisions/ui-title";
import Auth from "../routes/Auth";
import RoundNotStartedGate from "../components/round-not-started";
import FirstDecisionModal from "../components/first-decision-modal";
import { getRequest } from "../api/ApiService";
import { logError, detectMobile } from "../services/utils";
import Chart from "../components/finance/decisions/chart";
import CashBalanceModal from "../components/finance/decisions/cash-balance-modal";
import EventEmitter from "../services/event-emitter";
import RaiseCapitalButton from "../components/finance/decisions/raise-capital-button";
import RaiseCapitalSelectModal from "../components/finance/decisions/raise-capital-select-modal";
import RaiseEquityModal from "../components/finance/decisions/raise-equity-modal";
import RaiseDebtModal from "../components/finance/decisions/raise-debt-modal";
import ReturnCapitalButton from "../components/finance/decisions/return-capital-button";
import ReturnCapitalSelectModal from "../components/finance/decisions/return-capital-select-modal";
import ReturnDebtModal from "../components/finance/decisions/return-debt-modal";
import ReturnEquityModal from "../components/finance/decisions/return-equity-modal";
import DebtDecisionsPage from "../components/finance/decisions/debt-decisions-page";
import EquityDecisionsPage from "../components/finance/decisions/equity-decisions-page";
import WarningAlert from "../components/alerts/warning";
import Fragment from 'react-dot-fragment';
import { Modal } from "react-bootstrap";
import styled from "styled-components";

import {
  submitDecisions,
} from "../api/SubmitDecisions";
import { fetchTeamDecisions, fetchTeamStatus } from "../api/TeamDecisionsService";
import Styled from "components/finance/style";
import ArrowSvg from "components/pm/arrow.svg";
import { view } from "@risingstack/react-easy-state";
import SweetAlert from "sweetalert-react";
import LastStepModal from "../components/get-started/last-step";
import { moneyFormat } from '../components/analysis/format';

import IconTableHelp from "./assets/icon-table-help.svg";

import CapitalStructureVideo from './assets/finance-capital-structure.mp4';
import LendingRateVideo from './assets/finance-lending-rate.mp4';
import DebtToCapitalVideo from './assets/finance-debt-to-capital.mp4';
import InterestChargedVideo from './assets/finance-interest-charged.mp4';

// Hover tooltip that pairs with a KPI-label help icon. Wrap the icon
// in KpiInfoTooltipWrap and drop a KpiInfoTooltip alongside it; the
// tooltip fades in whenever the wrapper (or its focused descendant)
// is hovered/focused. Any existing onClick on the icon still fires.
const KpiInfoTooltipWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  &:hover > span.kpi-info-tooltip,
  &:focus-within > span.kpi-info-tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const KpiInfoTooltip = styled.span`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 260px;
  max-width: 340px;
  padding: 12px 14px;
  background: #EDF4FF;
  border: 1px solid #C7D8F0;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  font-family: 'Mukta Vaani', 'Lato', sans-serif;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  color: rgba(0, 27, 59, 0.85);
  text-align: left;
  white-space: normal;
  visibility: hidden;
  opacity: 0;
  transition: opacity 120ms ease-out;
  z-index: 100;
  pointer-events: none;

  &:after {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: #EDF4FF;
    border-left: 1px solid #C7D8F0;
    border-top: 1px solid #C7D8F0;
  }
`;

// Ownership formula shown inside the Team's Ownership tooltip:
// (Team's shares / Total outstanding shares) = X.XX%. Rendered as a
// centered visual fraction with a horizontal bar so students see the
// math rather than just the outcome.
const OwnershipFormula = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #C7D8F0;
  font-family: 'Mukta Vaani', 'Lato', sans-serif;
  color: rgba(0, 27, 59, 0.94);
`;

const OwnershipFraction = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;

const OwnershipNumerator = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`;

const OwnershipDenominator = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`;

const OwnershipBar = styled.span`
  display: block;
  width: 100%;
  min-width: 140px;
  height: 1.5px;
  background: rgba(0, 27, 59, 0.6);
`;

const OwnershipTermLabel = styled.span`
  font-weight: 500;
  font-size: 11px;
  line-height: 14px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 2px;
`;

const OwnershipEquals = styled.span`
  font-weight: 700;
  font-size: 20px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.75);
`;

const OwnershipResult = styled.span`
  font-weight: 700;
  font-size: 20px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);
`;

// Renders a dollar value in a layout where the "$" is left-anchored to
// a fixed slot and the digits are right-aligned within a tabular-nums
// container, so the "$" stays vertically aligned across rows even when
// numbers differ in magnitude. Use this inside SummaryCell.
const renderMoneyAligned = (n) => (
  <Styled.MoneyAligned>
    <Styled.MoneyPrefix>$</Styled.MoneyPrefix>
    <Styled.MoneyAmount>
      {Math.round(Number(n) || 0).toLocaleString('en-US')}
    </Styled.MoneyAmount>
  </Styled.MoneyAligned>
);

const videos = {
  'Capital Structure': CapitalStructureVideo,
  'Lending Rate': LendingRateVideo,
  'Debt to Capital': DebtToCapitalVideo,
  'Interest Charged': InterestChargedVideo,
};

class FinanceDecisions extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;

    const role = Auth.getRole();
    const hasAccess = role === "CEO" || role === "FI";
    const isOwnRole = role === "FI";

    this.state = {
      isOwnRole,
      hasAccess,
      submitDecisionsLoading: true,
      submission_data: {
        fn: {
          date: "N/A",
          unaccepted: 0
        }
      },
      projections_submitted: false,
      debt_to_capital: 0,
      cost_to_capital: 0,
      lending_rate: 0,
      loans: [],
      teamDebts: [],
      interest_payments: [],
      equity_cumulative: 0,
      equity: 0,
      equities: [],
      equity_clients: [],
      repurchased_shares: [],
      total_shares: 0,
      initialShares: 0,
      currentSharePrice: 0,
      companyOwnershipPct: 0,
      outstandingShares: 0,
      ending_cash: 0,
      prev_cash_balance: 0,
      financial_decisions: null,
      loading: true,
      roundInUrl: props.roundInUrl,
      currentRound: props.currentRound,
      rounds: props.rounds,
      unaccepted_decisions: {
        FI: 0,
        PM: 0,
        PD: 0,
        OP: 0
      },
      showNoProjectionsModal: false,
      isPreChecked: false,
      showManageFinancesModal: false,
      manageStep: 'main',
      showDebtDecisionsPage: false,
      showEquityDecisionsPage: false,
      // First-time coaching popup after the team's first equity
      // issuance in Round 1. See handleFirstEquityIssued below.
      firstDecisionModalOpen: false,
      interest_charge: 0,
      product_n_marketing: 0,
      operations: 0,
      net_cash_balance_from_finance: 0,
      showWarningModal: false,
      showAccruedInterestInfo: false,
      warningTitle: '',
      warningMessage: '',
      warningButtonText: '',
      isVideoModalShown: false,
      isVideoPlaying: false,
      selectedVideo: null
    };

    this.eventemitter = EventEmitter.getInstance();
    this.isMobile = detectMobile();

    this.videoRef = null;
  }

  handlePlayPause = () => {
    if (this.videoRef) {
      if (this.state.isVideoPlaying) {
        this.videoRef.pause();
      } else {
        this.videoRef.play();
      }
      this.setState({ isVideoPlaying: !this.state.isVideoPlaying });
    }
  };

  openVideoModal = (videoSrc) => {
    this.setState({
      isVideoModalShown: true,
      selectedVideo: videoSrc,
    });
  };

  closeVideoModal = () => {
    this.setState({
      isVideoModalShown: false,
      isVideoPlaying: false
    });
  };

  onHelpIconClick = (field) => {
    if (videos[field]) {
      this.openVideoModal(videos[field]);
    }
  };

  onNoProjectionsModalConfirm = () => {
    this.setState({
      showNoProjectionsModal: false
    });

    this.props.router.push(`/decisions/ceo?round_number=${this.roundNumber}`);
  };

  reloadRole = () => {
    const role = Auth.getRole();
    const hasAccess = role === "CEO" || role === "FI";
    const isOwnRole = role === "FI";

    this.setState({
      hasAccess,
      isOwnRole
    });
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.state.roundInUrl !== nextProps.roundInUrl ||
      this.state.currentRound !== nextProps.currentRound ||
      (this.state.rounds.length === 0 && nextProps.rounds.length > 0)
    ) {
      this.setState(
        {
          currentRound: nextProps.currentRound,
          roundInUrl: nextProps.roundInUrl,
          rounds: nextProps.rounds
        },
        () => {
          this.fetchData();
          this.reloadRole();
        }
      );
    }
  }

  get roundNumber() {
    const { currentRound, roundInUrl } = this.state;

    let round_number = currentRound;
    if (roundInUrl !== null && roundInUrl !== currentRound) {
      round_number = roundInUrl;
    }

    return round_number;
  }

  fetchData = (showWarnings) => {
    const { rounds } = this.state;

    if (rounds.length === 0) {
      return;
    }

    const round_number = this.roundNumber;
    const apiUrl = process.env.REACT_APP_API_URL;

    // Equity clients are a static lookup table (id -> name). Fire it in
    // parallel with team-decisions / team-status so the Equity Summary
    // Breakdown can resolve each equity[].client into an investor name.
    getRequest(`${apiUrl}static/equity_clients/`)
      .then(res => {
        if (!this._isMounted) return;
        this.setState({ equity_clients: res.body || [] });
      })
      .catch(() => {});

    Promise.all([
      fetchTeamDecisions(round_number, 'finance'),
      fetchTeamStatus(round_number),
    ])
      .then(([decisionsData, statusData]) => {
        if (!this._isMounted) return;

        if (showWarnings) {
          showWarnings(decisionsData);
        }

        const { financial_decisions, teamDebts, equities } = decisionsData;
        const interest_payments = decisionsData.interest_payments || [];
        const performance_moneys = decisionsData.performance_moneys || [];
        const post_moneys = decisionsData.post_moneys || [];
        const repurchased_shares = decisionsData.repurchased_shares || decisionsData.stock_repurchased || [];
        const constantsObj = decisionsData.constants || {};
        const { submission_data, unaccepted_decisions } = statusData;

        const {
          projections_submitted,
          debt_to_capital,
          cost_to_capital,
          loans,
          equity_cumulative,
          equity,
          ending_cash,
          prev_cash,
          product_n_marketing,
          operations,
          net_cash_balance_from_finance,
        } = financial_decisions.team;

        const { decisions } = decisionsData;
        // Auto-detect heuristic only fires on closed rounds (is_started
        // AND is_finished). On active rounds we don't pre-flag Finance
        // as Posted just because a decision counter is non-zero.
        const viewedRound = this.state.rounds.find(
          i => i.number === this.roundNumber);
        const isRoundEnded = !!viewedRound
          && viewedRound.is_started === true
          && viewedRound.is_finished === true;
        const isActiveRound = !!viewedRound
          && viewedRound.is_started === true
          && viewedRound.is_finished === false;

        // Current Share Price:
        //   • round 1 still active (i.e. round 1 has not ended)
        //                   → constants.initial_share_price
        //   • active round  → prior round's performance_moneys.share_price
        //   • otherwise     → viewed round's performance_moneys.share_price
        // Use the last matching row (handles multiple snapshots per round).
        const currentRoundId = viewedRound ? viewedRound.id : null;
        const priorRoundObj = this.state.rounds.find(
          r => r.number === Number(this.roundNumber) - 1);
        const priorRoundId = priorRoundObj ? priorRoundObj.id : null;
        const round1 = this.state.rounds.find(r => r.number === 1);
        const isRound1Active = !!round1 && round1.is_finished !== true;
        const targetRoundId = isActiveRound ? priorRoundId : currentRoundId;
        const matchingPerf = performance_moneys.filter(
          r => Number(r.round) === Number(targetRoundId));
        const currentSharePrice = isRound1Active
          ? Number(constantsObj.initial_share_price) || 0
          : (matchingPerf.length > 0
            ? Number(matchingPerf[matchingPerf.length - 1].share_price) || 0
            : 0);

        // Company Ownership % = constants.initial_shares / total outstanding shares
        // Total outstanding:
        //   • prefer current round post_moneys.shares
        //   • else fall back to prior round performance_moneys.shares
        const currentPostMoneys = post_moneys.filter(
          r => Number(r.round) === Number(currentRoundId));
        const currentPostMoneyShares = currentPostMoneys.length > 0
          ? Number(currentPostMoneys[currentPostMoneys.length - 1].shares) || 0
          : 0;
        const priorPerfForOwnership = performance_moneys.filter(
          r => Number(r.round) === Number(priorRoundId));
        const priorPerfShares = priorPerfForOwnership.length > 0
          ? Number(priorPerfForOwnership[priorPerfForOwnership.length - 1].shares) || 0
          : 0;
        const ownershipDenominator = currentPostMoneyShares || priorPerfShares;
        const initialShares = Number(constantsObj.initial_shares) || 0;
        const rawOwnershipPct = ownershipDenominator > 0
          ? (initialShares / ownershipDenominator) * 100
          : 0;
        // Clamp: the ratio is a fraction of ownership, so anything
        // outside [0, 100] is a data glitch (e.g. a stale denominator)
        // and displaying it that way just confuses students.
        const companyOwnershipPct = Math.max(0, Math.min(100, rawOwnershipPct));
        const outstandingShares = ownershipDenominator;

        // Finance pre-check: explicit user submission is the trigger,
        // matching PD/OP. We no longer auto-flip the button to Posted
        // based on cash or any decision counter — the user must
        // explicitly click POST on Finance.
        const isPreChecked = submission_data.fn && submission_data.fn.date !== 'N/A';

        const { teamInterestCharge, teamBalance } = teamDebts.reduce((acc, item) => {
          acc.teamInterestCharge += item.interest_charge;
          acc.teamBalance += item.balance;
          return acc;
        }, { teamInterestCharge: 0, teamBalance: 0 });

        const lending_rate = teamBalance ? teamInterestCharge / teamBalance : 0;

        let total_shares = 0;
        if (Array.isArray(equity)) {
          total_shares = equity.reduce((sum, e) => sum + (Number(e.issuance) || 0), 0);
        }

        this.setState({
          projections_submitted,
          debt_to_capital,
          cost_to_capital,
          loans,
          teamDebts,
          interest_payments,
          equity_cumulative,
          equity,
          equities: equities || [],
          repurchased_shares,
          total_shares,
          initialShares,
          outstandingShares,
          currentSharePrice,
          companyOwnershipPct,
          ending_cash,
          financial_decisions: { team: financial_decisions.team, products: financial_decisions.products },
          prev_cash_balance: prev_cash,
          submission_data,
          submitDecisionsLoading: false,
          loading: false,
          unaccepted_decisions,
          isPreChecked,
          lending_rate,
          interest_charge: teamInterestCharge,
          product_n_marketing,
          operations,
          net_cash_balance_from_finance,
        });
      })
      .catch(err => {
        logError(err, "FinanceDecisions", "fetchData");
      });
  };

  submitDecisions = () => {
    this.setState({
      submitDecisionsLoading: true
    });

    const { currentRound } = this.props;

    getRequest(`${process.env.REACT_APP_API_URL}team/page/financials/?round_number=${currentRound}`)
      .then(res => {
        const teamPageFinancials = res.body;
        const teamData = teamPageFinancials.team ? teamPageFinancials.team : null;
        const endingCash = teamData.ending_cash ? teamData.ending_cash : null;

        if (endingCash && endingCash < 0) {
          this.setState({
            showWarningModal: true,
            warningTitle: 'Warning! You Need More Cash',
            warningMessage: <Fragment>
              <p>Your finance decisions were successfully posted, but you still have insufficient capital.</p><br />
              <p>In order for your decisions to be fully accepted, your cash balance on the finance decision page needs to have a positive value. </p></Fragment>,
            warningButtonText: 'Done'
          });
        } else {
          submitDecisions("finance", this.state.roundInUrl || this.state.currentRound, (showWarnings) => {
            this.fetchData(showWarnings);
          });
        }
      })
      .catch(err => { });
  };

  get showActionButtons() {
    const { currentRound, roundInUrl, rounds, hasAccess } = this.state;
    const { roundShouldBeFinished } = this.props;

    if (
      hasAccess === false ||
      roundShouldBeFinished === true ||
      (roundInUrl !== null && roundInUrl !== currentRound)
    ) {
      return false;
    }

    const round = rounds.find(i => i.number === currentRound);
    if (!round) {
      return false;
    }

    return round.is_started === true && round.is_finished === false;
  }

  get debtToCapital() {
    const { debt_to_capital } = this.state;

    return debt_to_capital > 0 ? (debt_to_capital * 100).toFixed(2) : "";
  }

  get costOfCapital() {
    const { cost_to_capital } = this.state;

    return cost_to_capital > 0 ? (cost_to_capital * 100).toFixed(2) : "";
  }

  get interestCharge() {
    const { interest_charge } = this.state;

    return interest_charge > 0 ? moneyFormat(Math.abs(interest_charge)) : "";
  }

  get lendingRate() {
    const { lending_rate } = this.state;

    return lending_rate > 0 ? (lending_rate * 100).toFixed(2) : "";
  }

  get healthBadgeStatus() {
    const ratio = Number(this.state.debt_to_capital) || 0;
    if (ratio <= 0) return null;
    const pct = ratio * 100;
    if (pct <= 40) return 'Healthy';
    if (pct <= 70) return 'Moderate';
    if (pct <= 80) return 'High';
    return 'Very High';
  }

  // A round is "active" when it's started but not yet finished. On an
  // active round, interest for the current round is still accruing, so
  // we display prior_year_interest_payable (the finalized carry-in from
  // last year). On a closed round, interest_payable is finalized and is
  // what we display. Falls back to false when we can't identify the
  // viewed round (safer default: treat as closed / use interest_payable).
  get isActiveRound() {
    const { rounds, currentRound, roundInUrl } = this.state;
    const roundNum = roundInUrl != null ? roundInUrl : currentRound;
    const round = (rounds || []).find(r => r.number === roundNum);
    return !!round && round.is_started === true && round.is_finished === false;
  }

  get totalOutstandingDebt() {
    const { teamDebts, interest_payments } = this.state;
    if (!Array.isArray(teamDebts)) return 0;
    const isActive = this.isActiveRound;
    // Two interest buckets live on each debt row:
    //   prior_year_interest_payable → due THIS round (interest accrued
    //     last round). interest_payments posted this round are paid
    //     against this bucket.
    //   interest_payable → interest just accrued THIS round. Rolls into
    //     next round as prior_year_interest_payable; nothing's been
    //     paid against it yet.
    // Pick the field that matches the round's state.
    const interestField = isActive ? 'prior_year_interest_payable' : 'interest_payable';
    // Floor per row so the KPI matches the integer used as the repay
    // cap on the Choose Your Lender page (see debt-decisions-page.js).
    const grossOutstanding = teamDebts.reduce(
      (sum, d) => sum + (Number(d.balance) || 0) + Math.floor(Number(d[interestField]) || 0),
      0
    );
    // Only subtract interest payments during an active round — those
    // payments reduce prior_year_interest_payable, not interest_payable.
    // Also only count payments that target a debt row still present
    // in teamDebts; the response can carry payments against debts
    // closed in prior rounds, and summing those drags the KPI below
    // the actual balance.
    const currentDebtIds = new Set(teamDebts.map(d => Number(d.id)));
    const interestPaidThisRound = isActive
      ? (Array.isArray(interest_payments) ? interest_payments : [])
          .filter(ip => currentDebtIds.has(Number(ip.debt)))
          .reduce((sum, ip) => sum + (Number(ip.amount) || 0), 0)
      : 0;
    return Math.max(0, grossOutstanding - interestPaidThisRound);
  }

  // Principal-only outstanding debt (no accrued interest). Used by the
  // Capital Structure donut so the chart compares the same kind of
  // capital on both sides (debt principal vs. equity capital raised).
  get totalOutstandingDebtPrincipal() {
    const { teamDebts } = this.state;
    if (!Array.isArray(teamDebts)) return 0;
    return teamDebts.reduce(
      (sum, d) => sum + (Number(d.balance) || 0),
      0
    );
  }

  get totalAccruedInterest() {
    const { teamDebts, interest_payments } = this.state;
    if (!Array.isArray(teamDebts)) return 0;
    const isActive = this.isActiveRound;
    const field = isActive ? 'prior_year_interest_payable' : 'interest_payable';
    // Floor per row (same rationale as totalOutstandingDebt).
    const grossInterest = teamDebts.reduce((sum, d) => sum + Math.floor(Number(d[field]) || 0), 0);
    // Only subtract interest payments made against still-live debts
    // during an active round. See totalOutstandingDebt for the full
    // rationale on both gates (bucket mismatch after close + stale
    // payments against debts closed in prior rounds).
    const currentDebtIds = new Set(teamDebts.map(d => Number(d.id)));
    const interestPaidThisRound = isActive
      ? (Array.isArray(interest_payments) ? interest_payments : [])
          .filter(ip => currentDebtIds.has(Number(ip.debt)))
          .reduce((sum, ip) => sum + (Number(ip.amount) || 0), 0)
      : 0;
    return Math.max(0, grossInterest - interestPaidThisRound);
  }

  get totalPrincipal() {
    const { teamDebts } = this.state;
    if (!Array.isArray(teamDebts)) return 0;
    return teamDebts.reduce((sum, d) => sum + (Number(d.balance) || 0), 0);
  }

  get totalCapitalRaised() {
    // Net of any stock buybacks: Σ equities.capital − Σ repurchased_shares.cost
    const repurchases = Array.isArray(this.state.repurchased_shares)
      ? this.state.repurchased_shares
      : [];
    const buybackCost = repurchases.reduce(
      (sum, r) => sum + (Number(r.cost) || 0), 0);
    const eq = this.state.equity;
    if (Array.isArray(eq)) {
      const gross = eq.reduce((sum, e) => sum + (Number(e.capital) || 0), 0);
      return gross - buybackCost;
    }
    return (Number(this.state.equity_cumulative) || 0) - buybackCost;
  }

  get weightedAverageRate() {
    const { teamDebts } = this.state;
    if (!Array.isArray(teamDebts) || teamDebts.length === 0) return 0;
    const totals = teamDebts.reduce((acc, d) => {
      const principal = Number(d.balance) || 0;
      const rate = Number(d.rate) || 0;
      acc.weighted += principal * rate;
      acc.principal += principal;
      return acc;
    }, { weighted: 0, principal: 0 });
    if (totals.principal <= 0) return 0;
    return (totals.weighted / totals.principal) * 100;
  }

  showManageFinancesModal = () => {
    this.setState({ showManageFinancesModal: true, manageStep: 'main' });
  };

  hideManageFinancesModal = () => {
    this.setState({ showManageFinancesModal: false, manageStep: 'main' });
  };

  pickManageBranch = (branch) => {
    if (branch === 'debt') {
      // Replace the Manage popup's debt sub-step with the new
      // Choose Your Lender page per the latest design.
      this.setState({ showManageFinancesModal: false, manageStep: 'main', showDebtDecisionsPage: true });
      return;
    }
    if (branch === 'equity') {
      // Replace the equity sub-step with the new Choose Your Equity
      // Investor page (mirrors the Lender page UX).
      this.setState({ showManageFinancesModal: false, manageStep: 'main', showEquityDecisionsPage: true });
      return;
    }
    this.setState({ manageStep: branch });
  };

  closeDebtDecisionsPage = () => {
    this.setState({ showDebtDecisionsPage: false });
  };

  closeEquityDecisionsPage = () => {
    this.setState({ showEquityDecisionsPage: false });
  };

  // Fired by EquityDecisionsPage after the team's first equity
  // issuance in Round 1. Shows the coaching modal that reminds the
  // student to also raise debt if equity isn't enough to cover
  // downstream product/marketing/operations spend.
  handleFirstEquityIssued = () => {
    this.setState({ firstDecisionModalOpen: true });
  };

  handleFirstDecisionClose = () => {
    this.setState({ firstDecisionModalOpen: false });
  };

  // CTA: close the popup, close the equity flow, and open the Cash
  // Balance modal so the user immediately sees how their raise
  // affects team cash.
  handleFirstDecisionCta = () => {
    this.setState({
      firstDecisionModalOpen: false,
      showEquityDecisionsPage: false,
    }, () => {
      this.eventemitter.trigger('show-cash-balance-modal');
    });
  };

  handleSelectLender = (lender) => {
    // For now, picking a lender from the Choose Your Lender page closes
    // the page and routes through the existing Raise Debt flow. The
    // backend chosen-lender pre-selection can be wired up in a follow-up.
    this.setState({ showDebtDecisionsPage: false }, () => {
      this.eventemitter.trigger("show-raise-debt-modal");
    });
  };

  backToManageMain = () => {
    this.setState({ manageStep: 'main' });
  };

  pickManageOption = (action) => {
    this.setState({ showManageFinancesModal: false, manageStep: 'main' }, () => {
      if (action === 'raise-debt') {
        this.eventemitter.trigger("show-raise-debt-modal");
      } else if (action === 'return-debt') {
        this.eventemitter.trigger("show-return-debt-modal");
      } else if (action === 'raise-equity') {
        const { projections_submitted } = this.state;
        if (!projections_submitted) {
          this.setState({ showNoProjectionsModal: true });
        } else {
          this.eventemitter.trigger("show-raise-equity-modal");
        }
      } else if (action === 'return-equity') {
        this.eventemitter.trigger("show-return-equity-modal");
      }
    });
  };

  get cashBalance() {
    const { ending_cash } = this.state;

    return ending_cash.toFixed(0);
  }

  showCashBalanceModal = () => {
    this.eventemitter.trigger("show-cash-balance-modal");
  };

  showRaiseCapitalSelectModal = () => {
    const { projections_submitted } = this.state;

    if (!projections_submitted) {
      this.setState({
        showNoProjectionsModal: true
      });
    } else {
      this.eventemitter.trigger("show-raise-capital-select-modal");
    }
  };

  showRaiseDebtModal = () => {
    this.eventemitter.trigger("hide-raise-capital-select-modal");
    this.eventemitter.trigger("show-raise-debt-modal");
  };

  showRaiseEquityModal = () => {
    this.eventemitter.trigger("hide-raise-capital-select-modal");
    this.eventemitter.trigger("show-raise-equity-modal");
  };

  showReturnCapitalSelectModal = () => {
    this.eventemitter.trigger("show-return-capital-select-modal");
  };

  showReturnDebtModal = () => {
    this.eventemitter.trigger("hide-return-capital-select-modal");
    this.eventemitter.trigger("show-return-debt-modal");
  };

  showReturnEquityModal = () => {
    this.eventemitter.trigger("hide-return-capital-select-modal");
    this.eventemitter.trigger("show-return-equity-modal");
  };

  get showGraph() {
    const { loans, equity_cumulative } = this.state;
    const debts = _.sumBy(loans, "amount");

    return debts > 0 || equity_cumulative > 0;
  }

  showInterestChargeWarning = () => {
    this.setState({
      showWarningModal: true,
      warningMessage: <Fragment>
        <b>Warning! Your Overall Interest Charged is High!</b> High interest expense could have an adverse impact in your Round 3 performance.
        Consider reducing your outstanding loans or at at the minimum, paying down your interest payable.
      </Fragment>,
      warningButtonText: 'Ok'
    });
  }
  hideWarningModal = () => {
    this.setState({
      showWarningModal: false,
    });
  }

  render() {
    // Lock the page only when no round has ever been started.
    // Does not fire for finished games (see ceo-decisions.js for the
    // full explanation).
    const hasEverStarted = (this.props.rounds || []).some(
      r => r && r.is_started === true
    );
    if (this.props.rounds_loaded && !hasEverStarted) {
      return <RoundNotStartedGate />;
    }

    const {
      isOwnRole,
      submitDecisionsLoading,
      submission_data,
      loading,
      loans,
      equity_cumulative,
      currentSharePrice,
      companyOwnershipPct,
      initialShares,
      outstandingShares,
      ending_cash,
      prev_cash_balance,
      currentRound,
      rounds,
      roundInUrl,
      financial_decisions,
      unaccepted_decisions,
      showNoProjectionsModal,
      isPreChecked,
      product_n_marketing,
      operations,
      net_cash_balance_from_finance,
      showWarningModal,
      showAccruedInterestInfo,
      warningTitle,
      warningMessage,
      warningButtonText,
      isVideoModalShown,
      isVideoPlaying
    } = this.state;
    const { location, router, currentRound: roundFromProps } = this.props;

    const hasAccess = this.showActionButtons;
    const roundNumber = this.roundNumber;
    const cashBalance = this.cashBalance;
    const round = isNil(roundInUrl) ? currentRound : roundInUrl;
    const roundId = rounds.find(i => i.number === Number(round)).id;

    return (
      <Styled.BodyWrapper>
        <LastStepModal
          location={location}
          router={router}
          url={`/decisions/finance?round=${roundFromProps}`}
        />
        <UiTitle
          isOwnRole={isOwnRole}
          hasAccess={hasAccess}
          submitDecisionsLoading={submitDecisionsLoading}
          submitDecisionsData={submission_data.fn}
          submitDecisions={this.submitDecisions}
          loading={loading}
          financial_decisions={financial_decisions}
          unaccepted_decisions={unaccepted_decisions}
          isPreChecked={isPreChecked}
          isRoundEnded={(() => {
            // Closed-round only: is_started AND is_finished both true
            // on the viewed round. Inline because finance-decisions
            // doesn't expose a shared getter for this yet.
            const viewedNumber = roundInUrl !== null ? roundInUrl : currentRound;
            const r = rounds.find(i => i.number === viewedNumber);
            return !!r && r.is_started === true && r.is_finished === true;
          })()}
        />
        <SweetAlert
          show={showNoProjectionsModal}
          title="Unsuccessful"
          text="You cannot raise capital without making financial projections. It’s the CEO role in this exercise."
          type="warning"
          onConfirm={this.onNoProjectionsModalConfirm}
          confirmButtonText="Make Projections"
        />
        <Styled.Subtitle>Raise and manage capital to finance product, marketing, and operations decisions.</Styled.Subtitle>
        {loading === true && <Loader type="ball-scale-ripple" active />}
        {loading === false && (
          <div>
            {hasAccess === true && (
              <Styled.ManageButtonBar>
                <Styled.ManageButton onClick={this.showManageFinancesModal}>
                  Manage Your Finances
                </Styled.ManageButton>
              </Styled.ManageButtonBar>
            )}

            {/* Underlying raise/return modals stay mounted to listen for events */}
            <RaiseDebtModal
              equity={equity_cumulative}
              roundNumber={roundNumber}
              reloadData={this.fetchData}
              showInterestChargeWarning={this.showInterestChargeWarning}
            />
            <RaiseEquityModal
              roundNumber={roundNumber}
              currentRound={currentRound}
              rounds={rounds}
              reloadData={this.fetchData}
            />
            <ReturnDebtModal
              roundNumber={roundNumber}
              cashBalance={cashBalance}
              reloadData={this.fetchData}
              roundId={roundId}
            />
            <ReturnEquityModal
              roundNumber={roundNumber}
              cashBalance={cashBalance}
              reloadData={this.fetchData}
            />

            {/* CashBalanceModal mounted but invisible until triggered via event */}
            <CashBalanceModal
              beginningCash={prev_cash_balance}
              ProductNMarketing={product_n_marketing}
              operations={operations}
              financing={net_cash_balance_from_finance}
              cashBalance={ending_cash}
            />

            {(() => {
              // Hide the Debt/Equity/Capital Structure blocks entirely
              // when there's no outstanding capital to summarize. Sums
              // are taken from the team-decisions API arrays:
              //   debts[].balance    → outstanding debt
              //   equities[].capital → outstanding equity
              const debtsArr = Array.isArray(this.state.teamDebts) ? this.state.teamDebts : [];
              const equitiesArr = Array.isArray(this.state.equities) ? this.state.equities : [];
              const outstandingDebtSum = debtsArr.reduce((s, d) => s + (Number(d.balance) || 0), 0);
              const outstandingEquitySum = equitiesArr.reduce((s, e) => s + (Number(e.capital) || 0), 0);
              const hasDebt = outstandingDebtSum > 0;
              const hasEquity = outstandingEquitySum > 0;
              return (
            <div>
            <Styled.KpiRow cardCount={hasDebt ? 5 : 3}>
              <Styled.KpiCard>
                <Styled.KpiLabel>
                  Cash on Hand
                  <KpiInfoTooltipWrap tabIndex={0} aria-label="What is Cash on Hand?">
                    <Styled.HelpIcon
                      src={IconTableHelp}
                      onClick={this.showCashBalanceModal}
                      title="View cash balance breakdown"
                    />
                    <KpiInfoTooltip className="kpi-info-tooltip" role="tooltip">
                      How much cash your team currently has this round. Click for a detailed breakdown.
                    </KpiInfoTooltip>
                  </KpiInfoTooltipWrap>
                </Styled.KpiLabel>
                <Styled.KpiValue>
                  <NumberFormat
                    value={cashBalance}
                    displayType="text"
                    prefix="$"
                    thousandSeparator=","
                  />
                </Styled.KpiValue>
                {Number(cashBalance) < 0 && (
                  <Styled.HealthBadge status="High">
                    Insufficient Capital
                  </Styled.HealthBadge>
                )}
              </Styled.KpiCard>

              {hasDebt && (
                <Styled.KpiCard>
                  <Styled.KpiLabel>Outstanding Debt</Styled.KpiLabel>
                  <Styled.KpiValue>{moneyFormat(this.totalOutstandingDebt)}</Styled.KpiValue>
                </Styled.KpiCard>
              )}

              {hasDebt && (
                <Styled.KpiCard>
                  <Styled.KpiLabel>
                    Debt-to-Capital
                    <Styled.HelpIcon src={IconTableHelp} onClick={() => this.onHelpIconClick("Debt to Capital")} />
                  </Styled.KpiLabel>
                  <Styled.KpiValue>{this.debtToCapital ? `${this.debtToCapital}%` : '0%'}</Styled.KpiValue>
                  {this.healthBadgeStatus && (
                    <Styled.HealthBadge status={this.healthBadgeStatus}>
                      {this.healthBadgeStatus}
                    </Styled.HealthBadge>
                  )}
                </Styled.KpiCard>
              )}

              <Styled.KpiCard>
                <Styled.KpiLabel>
                  Team's Ownership %
                  <KpiInfoTooltipWrap tabIndex={0} aria-label="What is Team's Ownership %?">
                    <Styled.HelpIcon src={IconTableHelp} />
                    <KpiInfoTooltip className="kpi-info-tooltip" role="tooltip">
                      The share of the company your team still holds. It's your original founding shares divided by every share outstanding this round (founding shares + any new shares issued to investors).
                      <OwnershipFormula aria-hidden>
                        <OwnershipFraction>
                          <OwnershipNumerator>
                            {Number(initialShares || 0).toLocaleString("en-US")}
                            <OwnershipTermLabel>Your team's shares</OwnershipTermLabel>
                          </OwnershipNumerator>
                          <OwnershipBar />
                          <OwnershipDenominator>
                            {Number(outstandingShares || 0).toLocaleString("en-US")}
                            <OwnershipTermLabel>Total outstanding shares</OwnershipTermLabel>
                          </OwnershipDenominator>
                        </OwnershipFraction>
                        <OwnershipEquals>=</OwnershipEquals>
                        <OwnershipResult>{`${Number(companyOwnershipPct || 0).toFixed(2)}%`}</OwnershipResult>
                      </OwnershipFormula>
                    </KpiInfoTooltip>
                  </KpiInfoTooltipWrap>
                </Styled.KpiLabel>
                <Styled.KpiValue>{`${Number(companyOwnershipPct || 0).toFixed(2)}%`}</Styled.KpiValue>
              </Styled.KpiCard>

              <Styled.KpiCard>
                <Styled.KpiLabel>
                  Current Share Price
                  <KpiInfoTooltipWrap tabIndex={0} aria-label="What is Current Share Price?">
                    <Styled.HelpIcon src={IconTableHelp} />
                    <KpiInfoTooltip className="kpi-info-tooltip" role="tooltip">
                      Your stock price for this round. It updates each round based on your team's performance. It's calculated by taking your company's market value and dividing it by your company's outstanding shares. In Years 1 and 2, market value is driven by revenues; in Year 3, it's driven by profits.
                    </KpiInfoTooltip>
                  </KpiInfoTooltipWrap>
                </Styled.KpiLabel>
                <Styled.KpiValue>${Number(currentSharePrice || 0).toFixed(2)}</Styled.KpiValue>
              </Styled.KpiCard>
            </Styled.KpiRow>

            {(hasDebt || hasEquity) && (
            <Styled.TwoColumnSection>
            {hasDebt && (
            <Styled.SectionCard>
              <Styled.SectionHeader>
                <Styled.SectionTitle>Debt Summary Breakdown</Styled.SectionTitle>
                <Styled.SectionSubtitleRow>
                  <Styled.SectionSubtitleRow_Inner>Outstanding debt</Styled.SectionSubtitleRow_Inner>
                  <Styled.SectionSubtitleValue>{moneyFormat(this.totalOutstandingDebt)}</Styled.SectionSubtitleValue>
                </Styled.SectionSubtitleRow>
              </Styled.SectionHeader>
              <Styled.SummaryTable>
                <thead>
                  <Styled.SummaryHeadRow>
                    <Styled.SummaryHeadCell>Lenders</Styled.SummaryHeadCell>
                    <Styled.SummaryHeadCell align="right">Principal</Styled.SummaryHeadCell>
                    <Styled.SummaryHeadCell align="right">
                      Accrued Interest
                      <Styled.HelpIconInline
                        src={IconTableHelp}
                        onClick={() => this.setState({ showAccruedInterestInfo: true })}
                        title="What is accrued interest?"
                      />
                    </Styled.SummaryHeadCell>
                    <Styled.SummaryHeadCell align="right">Rate</Styled.SummaryHeadCell>
                  </Styled.SummaryHeadRow>
                </thead>
                <tbody>
                  {(() => {
                    const debts = (this.state.teamDebts || []).filter(d => Number(d.balance) > 0);
                    if (debts.length === 0) {
                      return (
                        <Styled.SummaryRow>
                          <Styled.SummaryCell colSpan="4" style={{ color: 'rgba(0,27,59,0.5)' }}>
                            No outstanding debt
                          </Styled.SummaryCell>
                        </Styled.SummaryRow>
                      );
                    }
                    const isActive = this.isActiveRound;
                    const interestField = isActive ? 'prior_year_interest_payable' : 'interest_payable';
                    const interestPayments = Array.isArray(this.state.interest_payments) ? this.state.interest_payments : [];
                    return debts.map((d, i) => {
                      const principal = Number(d.balance) || 0;
                      // Floor per row (same rationale as totalAccruedInterest).
                      const grossAccrued = Math.floor(Number(d[interestField]) || 0);
                      // Offset by any interest payment already posted
                      // against THIS debt row, during the active round
                      // only. Match on debt id (unique per row) rather
                      // than client — a client can carry multiple
                      // debts over time, and matching on client picks
                      // the first interest payment chronologically,
                      // which can be a stale payment against an old
                      // debt no longer in teamDebts. Same gate as
                      // totalOutstandingDebt.
                      const ip = isActive
                        ? interestPayments.find(p => Number(p.debt) === Number(d.id))
                        : null;
                      const paidThisRound = ip ? Number(ip.amount) || 0 : 0;
                      const accruedInterest = Math.max(0, grossAccrued - paidThisRound);
                      const rate = d.rate != null ? (Number(d.rate) * 100).toFixed(2) : null;
                      // Resolve the lender name by matching
                      // debts[i].client → loans[].client_id. The previous
                      // code used positional index into loans, which is
                      // ordered by client_id (Prudent, Mezz, Great White)
                      // regardless of which lender the team actually
                      // borrowed from. That mismatch rendered "Prudent
                      // Bank" on a Great White Financials loan.
                      const loanMatch = Array.isArray(loans)
                        ? loans.find(l => Number(l.client_id) === Number(d.client))
                        : null;
                      const name = d.client_name
                        || (loanMatch && loanMatch.client_name)
                        || `Loan ${i + 1}`;
                      return (
                        <Styled.SummaryRow key={i}>
                          <Styled.SummaryCell>{name}</Styled.SummaryCell>
                          <Styled.SummaryCell align="right">{renderMoneyAligned(principal)}</Styled.SummaryCell>
                          <Styled.SummaryCell align="right">{accruedInterest > 0 ? renderMoneyAligned(accruedInterest) : '-'}</Styled.SummaryCell>
                          <Styled.SummaryCell align="right" accent>{rate !== null ? `${rate}%` : '-'}</Styled.SummaryCell>
                        </Styled.SummaryRow>
                      );
                    });
                  })()}
                </tbody>
              </Styled.SummaryTable>
              <Styled.SummaryFooter>
                <Styled.SummaryFooterLabel>
                  Weighted Average Interest Rate
                  <Styled.HelpIcon src={IconTableHelp} onClick={() => this.onHelpIconClick("Lending Rate")} />
                </Styled.SummaryFooterLabel>
                <Styled.SummaryFooterValue>
                  {this.weightedAverageRate > 0 ? `${this.weightedAverageRate.toFixed(2)}%` : '0%'}
                </Styled.SummaryFooterValue>
              </Styled.SummaryFooter>
            </Styled.SectionCard>
            )}

            {hasEquity && (
            <Styled.SectionCard>
              <Styled.SectionHeader>
                <Styled.SectionTitle>Equity Summary Breakdown</Styled.SectionTitle>
                <Styled.SectionSubtitleRow>
                  <Styled.SectionSubtitleRow_Inner>Outstanding Equity</Styled.SectionSubtitleRow_Inner>
                  <Styled.SectionSubtitleValue>{moneyFormat(this.totalCapitalRaised)}</Styled.SectionSubtitleValue>
                </Styled.SectionSubtitleRow>
              </Styled.SectionHeader>
              <Styled.SummaryTable>
                <thead>
                  <Styled.SummaryHeadRow>
                    <Styled.SummaryHeadCell>Equity Investor</Styled.SummaryHeadCell>
                    <Styled.SummaryHeadCell align="right">Net Capital Raised</Styled.SummaryHeadCell>
                    <Styled.SummaryHeadCell align="right">Shares</Styled.SummaryHeadCell>
                    <Styled.SummaryHeadCell align="right">Net Purchase Price</Styled.SummaryHeadCell>
                  </Styled.SummaryHeadRow>
                </thead>
                <tbody>
                  {(() => {
                    // The per-investor equity records live in the top-level
                    // `equities` array from the team-decisions response —
                    // NOT inside financial_decisions.team.equity.
                    const equityRecords = this.state.equities;
                    const equityClients = this.state.equity_clients || [];

                    // Build a clientId -> name lookup. equity_clients comes
                    // from /static/equity_clients/; each row maps an equity
                    // record's `client` FK to a readable investor name.
                    const clientNameById = {};
                    equityClients.forEach(c => { clientNameById[c.id] = c.name; });

                    // Group all equity entries with capital > 0 by client.
                    // (Each round may produce its own row in `equities` for the
                    // same investor; sum them so the table shows one row per
                    // investor.)
                    const rawEntries = Array.isArray(equityRecords) ? equityRecords : [];
                    const byClient = {};
                    rawEntries.forEach(e => {
                      const clientId = e.client;
                      if (!byClient[clientId]) {
                        byClient[clientId] = { client: clientId, capital: 0, issuance: 0 };
                      }
                      byClient[clientId].capital += Number(e.capital) || 0;
                      byClient[clientId].issuance += Number(e.issuance) || 0;
                    });

                    // Subtract buybacks per investor: Σ repurchased_shares.cost
                    // and Σ repurchased_shares.shares for each client.
                    const repurchases = Array.isArray(this.state.repurchased_shares)
                      ? this.state.repurchased_shares : [];
                    repurchases.forEach(r => {
                      const clientId = r.client;
                      if (clientId == null) return;
                      if (!byClient[clientId]) {
                        byClient[clientId] = { client: clientId, capital: 0, issuance: 0 };
                      }
                      byClient[clientId].capital -= Number(r.cost) || 0;
                      byClient[clientId].issuance -= Number(r.shares) || 0;
                    });

                    // Drop fully-exited investors (no remaining net stake).
                    const grouped = Object.values(byClient).filter(
                      g => Number(g.capital) > 0 || Number(g.issuance) > 0);

                    // Always-present founder row at the top of the
                    // table. Reflects the team's founder shares
                    // (constants.initial_shares) held at $0 cost basis.
                    const teamShares = Number(this.state.initialShares) || 0;
                    const teamRow = (
                      <Styled.SummaryRow key="team-founder-row">
                        <Styled.SummaryCell>Founders</Styled.SummaryCell>
                        <Styled.SummaryCell align="right">{renderMoneyAligned(0)}</Styled.SummaryCell>
                        <Styled.SummaryCell align="right">{teamShares.toLocaleString()}</Styled.SummaryCell>
                        <Styled.SummaryCell align="right" accent>$0.00</Styled.SummaryCell>
                      </Styled.SummaryRow>
                    );

                    if (grouped.length > 0) {
                      return [
                        teamRow,
                        ...grouped.map((g, i) => {
                          const cap = Number(g.capital) || 0;
                          const shares = Number(g.issuance) || 0;
                          const pps = shares > 0 ? cap / shares : null;
                          const name = clientNameById[g.client] || 'Equity Investor';
                          return (
                            <Styled.SummaryRow key={i}>
                              <Styled.SummaryCell>{name}</Styled.SummaryCell>
                              <Styled.SummaryCell align="right">{renderMoneyAligned(cap)}</Styled.SummaryCell>
                              <Styled.SummaryCell align="right">{shares.toLocaleString()}</Styled.SummaryCell>
                              <Styled.SummaryCell align="right" accent>{pps !== null ? `$${pps.toFixed(2)}` : '-'}</Styled.SummaryCell>
                            </Styled.SummaryRow>
                          );
                        })
                      ];
                    }
                    if (Number(equity_cumulative) > 0) {
                      const shares = Number(this.state.total_shares || 0);
                      const pps = shares > 0 ? equity_cumulative / shares : null;
                      return [
                        teamRow,
                        <Styled.SummaryRow key="common-equity">
                          <Styled.SummaryCell>Common Equity</Styled.SummaryCell>
                          <Styled.SummaryCell align="right">{renderMoneyAligned(equity_cumulative)}</Styled.SummaryCell>
                          <Styled.SummaryCell align="right">{shares.toLocaleString()}</Styled.SummaryCell>
                          <Styled.SummaryCell align="right" accent>{pps !== null ? `$${pps.toFixed(2)}` : '-'}</Styled.SummaryCell>
                        </Styled.SummaryRow>
                      ];
                    }
                    // No investor equity yet — still show the founder row alone.
                    return teamRow;
                  })()}
                </tbody>
              </Styled.SummaryTable>
            </Styled.SectionCard>
            )}
            </Styled.TwoColumnSection>
            )}
            </div>
            );
            })()}
          </div>
        )}

        {this.state.showDebtDecisionsPage && (() => {
          // Match the equity page's "active round" definition
          // (is_started && !is_finished). Drives which interest field
          // Outstanding Balance uses on the Lender cards.
          const viewedRound = this.state.rounds.find(
            i => i.number === Number(this.roundNumber));
          return (
            <DebtDecisionsPage
              roundNumber={this.roundNumber}
              roundId={viewedRound ? viewedRound.id : null}
              isActiveRound={
                !!viewedRound && viewedRound.is_started === true && viewedRound.is_finished === false
              }
              onClose={this.closeDebtDecisionsPage}
              onSelectLender={this.handleSelectLender}
              onGoToCeoProjections={() => {
                this.props.router.push(`/decisions/ceo?round_number=${this.roundNumber}`);
              }}
              onDecisionMade={this.fetchData}
            />
          );
        })()}

        {this.state.showEquityDecisionsPage && (() => {
          // A round is "active" only when it's started AND not yet
          // finished (general-info api flags). This correctly returns
          // false after the final round ends, even though game.round
          // still points at it.
          const viewedRound = this.state.rounds.find(
            i => i.number === Number(this.roundNumber));
          return (
            <EquityDecisionsPage
              roundNumber={this.roundNumber}
              roundId={viewedRound ? viewedRound.id : null}
              isActiveRound={
                !!viewedRound && viewedRound.is_started === true && viewedRound.is_finished === false
              }
              onClose={this.closeEquityDecisionsPage}
              onGoToCeoProjections={() => {
                this.props.router.push(`/decisions/ceo?round_number=${this.roundNumber}`);
              }}
              onDecisionMade={this.fetchData}
              onFirstEquityIssued={this.handleFirstEquityIssued}
            />
          );
        })()}

        <FirstDecisionModal
          open={this.state.firstDecisionModalOpen}
          title="Nice work on your first capital raise!"
          body="You can update this any time before the round deadline. Make sure to raise enough capital to cover product, marketing, and operations spend. If equity alone isn't enough, also raise debt (borrow from lenders) to cover the gap. Otherwise your team won't be able to post decisions and will get a 0% score for the round."
          ctaLabel="View Cash Balance"
          onCta={this.handleFirstDecisionCta}
          onClose={this.handleFirstDecisionClose}
        />

        {this.state.showManageFinancesModal && (
          <Styled.ManageOverlay onClick={this.hideManageFinancesModal}>
            <Styled.ManageModal onClick={e => e.stopPropagation()}>
              <Styled.ManageModalTitle>Manage Your Finances</Styled.ManageModalTitle>
              <Styled.ManageModalSubtitle>Select a decision to explore</Styled.ManageModalSubtitle>
              {this.state.manageStep === 'main' && (() => {
                const hasEquity = Number(this.totalCapitalRaised) > 0 || Number(this.state.equity_cumulative) > 0;
                return (
                <Styled.ManageGrid>
                  <Styled.ManageOptionWrapper>
                    <Styled.ManageOption
                      onClick={() => hasEquity && this.pickManageBranch('debt')}
                      disabled={!hasEquity}
                    >
                      Debt Decisions
                    </Styled.ManageOption>
                    {!hasEquity && (
                      <Styled.ManageOptionTooltip>
                        You have to raise equity before you can take on debt.
                      </Styled.ManageOptionTooltip>
                    )}
                  </Styled.ManageOptionWrapper>
                  <Styled.ManageOption onClick={() => this.pickManageBranch('equity')}>
                    Equity Decisions
                  </Styled.ManageOption>
                </Styled.ManageGrid>
                );
              })()}
              {this.state.manageStep === 'debt' && (
                <Styled.ManageGrid>
                  <Styled.ManageOption onClick={() => this.pickManageOption('raise-debt')}>
                    Raise Debt
                  </Styled.ManageOption>
                  <Styled.ManageOption onClick={() => this.pickManageOption('return-debt')}>
                    Return Debt
                  </Styled.ManageOption>
                </Styled.ManageGrid>
              )}
              {this.state.manageStep === 'equity' && (
                <Styled.ManageGrid>
                  <Styled.ManageOption onClick={() => this.pickManageOption('raise-equity')}>
                    Raise Equity
                  </Styled.ManageOption>
                  <Styled.ManageOption
                    onClick={() => this.pickManageOption('return-equity')}
                    disabled={round < 2}
                    style={round < 2 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    Return Equity
                  </Styled.ManageOption>
                </Styled.ManageGrid>
              )}
              <Styled.ManageCloseRow>
                {this.state.manageStep !== 'main' && (
                  <Styled.ManageCloseButton onClick={this.backToManageMain} style={{ marginRight: 'auto' }}>
                    ← Back
                  </Styled.ManageCloseButton>
                )}
                <Styled.ManageCloseButton onClick={this.hideManageFinancesModal}>
                  Cancel
                </Styled.ManageCloseButton>
              </Styled.ManageCloseRow>
            </Styled.ManageModal>
          </Styled.ManageOverlay>
        )}

        {showWarningModal && <WarningAlert
          title={warningTitle}
          message={warningMessage}
          onSuccess={this.hideWarningModal}
          onCancel={this.hideWarningModal}
          successButtonText={warningButtonText}
          showCancel={false}
        />}

        {showAccruedInterestInfo && (
          <Styled.LenderNoticeOverlay onClick={() => this.setState({ showAccruedInterestInfo: false })}>
            <Styled.LenderNoticeModal onClick={e => e.stopPropagation()}>
              <Styled.LenderNoticeTitle>Accrued Interest</Styled.LenderNoticeTitle>
              <Styled.LenderNoticeMessage>
                Accrued interest is the cumulative interest that has built up on a loan since the beginning. New interest accrues at the end of each year on any outstanding balance.
              </Styled.LenderNoticeMessage>
              <Styled.LenderNoticeActions>
                <Styled.LenderNoticeButton onClick={() => this.setState({ showAccruedInterestInfo: false })}>OK</Styled.LenderNoticeButton>
              </Styled.LenderNoticeActions>
            </Styled.LenderNoticeModal>
          </Styled.LenderNoticeOverlay>
        )}

        <Modal show={isVideoModalShown} onHide={this.closeVideoModal} className="goals-and-targets-modal">
          <Modal.Body>
            <Styled.ModalVideoWrapper className={isVideoPlaying ? 'is-playing' : ''}>
              <video
                ref={(ref) => { this.videoRef = ref; }}
                src={this.state.selectedVideo}
                alt="Welcome Video"
                onClick={this.handlePlayPause}
                onEnded={() => this.setState({ isVideoPlaying: false })}
                style={{ width: '100%', borderRadius: 10 }}
              />
              <Styled.PlayPauseButton onClick={this.handlePlayPause}>
                {isVideoPlaying ? '❚❚' : '▶'}
              </Styled.PlayPauseButton>
              <Styled.CloseModalVideoButton onClick={this.closeVideoModal}>×</Styled.CloseModalVideoButton>
            </Styled.ModalVideoWrapper>
          </Modal.Body>
        </Modal>
      </Styled.BodyWrapper>
    );
  }
}

export default view(FinanceDecisions);



// WEBPACK FOOTER //
// ./src/containers/finance-decisions.js